// src/resources/supportChats.js

import React, { useEffect, useState, useRef } from 'react'; 
import {
  List,
  Show,
  SimpleShowLayout,
  useRecordContext,
  useNotify,
  TextInput,
  SimpleForm,
  Button,
  FunctionField,
  Datagrid,
  useDataProvider,
} from 'react-admin';
import { Box, Paper, Typography, CircularProgress } from '@mui/material';
import { useSocket } from '../SocketContext'; 

// المكون الخاص بعرض المحادثة والسماح بالرد
const ChatView = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const dataProvider = useDataProvider();
    const socket = useSocket(); 
    const messagesEndRef = useRef(null); 

    const [messages, setMessages] = useState([]);
    const [isSending, setIsSending] = useState(false);

    // دالة لتحريك الـ scroll لأسفل
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    // Hook 1: لتهيئة الرسائل
    useEffect(() => {
        if (record && record.messages) {
            setMessages(record.messages);
        }
    }, [record]); 

    // Hook 2: للاستماع للرسائل الجديدة
    useEffect(() => {
        if (!socket || !record) return;

        const handleNewMessage = (data) => {
            if (data.chatId === record.id) {
                console.log('New support message received, appending to list...');
                setMessages(prevMessages => [...prevMessages, data.message]);
            }
        };
        
        socket.on('admin_new_support_message', handleNewMessage);

        return () => {
            socket.off('admin_new_support_message', handleNewMessage);
        };
    }, [socket, record]); 

    // Hook 3: لتحريك الـ scroll
    useEffect(() => {
        scrollToBottom();
    }, [messages]); 

    if (!record || !record.members) {
         return <CircularProgress />; 
    }

    const handleSend = (values) => {
        if (!values.content || values.content.trim() === '' || isSending) return;
        
        setIsSending(true); 
        
        dataProvider.create(`support-chats/${record.id}/messages`, { data: values })
            .then((response) => {
                setMessages(prevMessages => [...prevMessages, response.data]);
                setIsSending(false);
            })
            .catch(error => {
                notify(`Error: ${error.message || 'Could not send message'}`, { type: 'warning' });
                setIsSending(false); 
            });
    };

    return (
        <Box mt={2}>
            <Typography variant="h6">
                Chat with: {record.members.find(m => m.user?.name !== 'Admin User')?.user?.name || 'Unknown User'}
            </Typography>
            <Paper sx={{ maxHeight: '50vh', overflow: 'auto', padding: 2, mb: 2, mt: 1 }}>
                {messages.map(msg => {
                    
                    // --- ======================================== ---
                    // ---           **الإصلاح الجذري هنا** ---
                    // --- ======================================== ---
                    let messageContent;
                    const isUrl = msg.content && (msg.content.startsWith('http') || msg.content.startsWith('https'));

                    // التحقق إذا كان الرابط مرسلاً (سواء كان نصياً أو صوتياً)
                    if (isUrl) {
                        // التحقق من الامتدادات
                        const isVideo = msg.content.endsWith('.mp4');
                        const isAudio = msg.content.endsWith('.mp3') || msg.content.endsWith('.m4a') || msg.content.endsWith('.ogg') || msg.content.endsWith('.aac');

                        if (isVideo) {
                            // إذا كان الامتداد mp4، استخدم <video>
                            messageContent = (
                                <video controls style={{ maxWidth: '250px', borderRadius: '8px' }}>
                                    <source src={msg.content} type="video/mp4" />
                                    Your browser does not support the video element.
                                </video>
                            );
                        } else if (isAudio) {
                            // إذا كان امتدادًا صوتيًا، استخدم <audio>
                            messageContent = (
                                <audio controls style={{ maxWidth: '250px' }}>
                                    <source src={msg.content} />
                                    Your browser does not support the audio element.
                                </audio>
                            );
                        } else if (msg.type === 'text') {
                             // إذا كان نصًا عاديًا (يحتوي على رابط مثلاً)
                             messageContent = <Typography>{msg.content}</Typography>;
                        } else {
                            // إذا كان نوعه "audio" ولكن الامتداد غير معروف، اعرض الرابط
                            messageContent = <Typography component="a" href={msg.content} target="_blank" rel="noopener noreferrer">{msg.content}</Typography>;
                        }
                    } else {
                        // إذا كان نوع الرسالة "text" وبدون رابط
                        messageContent = <Typography>{msg.content}</Typography>;
                    }
                    // --- ======================================== ---
                    // ---           **نهاية الإصلاح** ---
                    // --- ======================================== ---

                    return (
                        <Box key={msg.id} my={1} display="flex" flexDirection="column" alignItems={msg.user?.name === 'Admin User' ? 'flex-end' : 'flex-start'}>
                            <Typography variant="caption" color="textSecondary">{msg.user?.name || 'Deleted User'}</Typography>
                            <Paper elevation={2} sx={{ display: 'inline-block', padding: '6px 12px', borderRadius: '10px', backgroundColor: msg.user?.name === 'Admin User' ? 'primary.light' : 'background.paper' }}>
                                {messageContent} {/* <-- استخدام المحتوى الديناميكي (نص أو مشغل صوتي/فيديو) */}
                            </Paper>
                        </Box>
                    );
                })}
                <div ref={messagesEndRef} />
            </Paper>
            
            <SimpleForm onSubmit={handleSend} toolbar={<></>} defaultValues={{ content: '' }}>
                <TextInput source="content" fullWidth resettable label={false} placeholder="Type a message..." disabled={isSending} />
                <Button type="submit" label={isSending ? "Sending..." : "Send"} variant="contained" sx={{ mt: 1 }} disabled={isSending} />
            </SimpleForm>
        </Box>
    );
};

// --- قائمة المحادثات (تم إضافة التحققات الآمنة سابقاً) ---
export const SupportChatList = (props) => (
    <List {...props}>
        <Datagrid rowClick="show">
            <FunctionField 
                label="User" 
                render={record => {
                    if (!record || !record.members) return 'N/A'; 
                    const userMember = record.members.find(m => m.user?.name !== 'Admin User');
                    return userMember?.user?.name || 'Unknown User';
                }} 
            />
            <FunctionField 
                label="Last Message" 
                render={record => {
                    if (!record || !record.messages || record.messages.length === 0) {
                        return 'No messages yet';
                    }
                    // **تحسين إضافي**: عرض "رسالة صوتية/فيديو" في القائمة
                    if (record.messages[0].type === 'audio') {
                        // التحقق من الامتداد
                        if (record.messages[0].content.endsWith('.mp4')) {
                            return '[Video Message]';
                        }
                        return '[Voice Message]';
                    }
                    return record.messages[0].content;
                }} 
                sx={{
                    maxWidth: '400px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            />
            <FunctionField label="ID" render={record => record ? `...${record.id.slice(-6)}` : ''} />
        </Datagrid>
    </List>
);

// --- شاشة عرض المحادثة (لا تغيير هنا) ---
export const SupportChatShow = (props) => (
    <Show {...props} actions={null}>
        <SimpleShowLayout>
            <ChatView />
        </SimpleShowLayout>
    </Show>
);